import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { injectIntl } from 'react-intl'
import { FilterTable } from 'gnos-tables'
import cx from 'classnames'
import _ from 'lodash'

import NoData from '../../../../../../../../../components/Workspace/components/NoData'
import InactivityIndicatorHeader from '../../../../../../../../../../../components/TableCell/components/InactiveIndicator/Header'
import InactiveIndicatorCell from '../../../../../../../../../../../components/TableCell/components/InactiveIndicator/Cell'
import Entity from '../../../../../../../../../../../components/TableCell/components/Entity'
import CustomDate from '../../../../../../../../../../../components/CustomDate'
import Link from '../../../../../../../../../../../components/TableCell/components/Link'
import FailedLoad from '../../../../../../../../../components/Workspace/components/FailedLoad'

import { LOCALE_PREFIX } from '../../constants'

import hocConnect from './hocConnect'
import './style.scss'

@injectIntl
@hocConnect
export default class Bankrupt extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        companyAsCreditorLoadTableData: PropTypes.func.isRequired,
        companyAsCreditorTableChange: PropTypes.func.isRequired,
        customData: PropTypes.object,
        data: PropTypes.array,
        dataLocale: PropTypes.string,
        failed: PropTypes.bool,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object,
        isLoading: PropTypes.bool,
        level: PropTypes.string.isRequired,
        loaded: PropTypes.bool,
        locale: PropTypes.string.isRequired,
        locked: PropTypes.bool,
        module: PropTypes.string.isRequired,
        pagination: PropTypes.object,
        filter: PropTypes.object,
        sorted: PropTypes.array,
        tableId: PropTypes.string.isRequired,
        tableSelector: PropTypes.string.isRequired,
    }

    componentDidMount() {
        const { loaded, dataLocale, locale } = this.props

        if (!loaded || dataLocale !== locale) this.loadData()
    }

    shouldComponentUpdate(nextProps) {
        const {
            customData,
            data,
            isLoading,
            level,
            locale,
            pagination,
            sorted,
            tableId,
            failed,
            loaded,
            tableSelector,
            filter,
        } = this.props

        if (!_.isEqual(customData, nextProps.customData)) return true
        if (!_.isEqual(data, nextProps.data)) return true
        if (!_.isEqual(isLoading, nextProps.isLoading)) return true
        if (!_.isEqual(level, nextProps.level)) return true
        if (!_.isEqual(locale, nextProps.locale)) return true
        if (!_.isEqual(pagination, nextProps.pagination)) return true
        if (!_.isEqual(sorted, nextProps.sorted)) return true
        if (!_.isEqual(failed, nextProps.failed)) return true
        if (!_.isEqual(loaded, nextProps.loaded)) return true
        if (!_.isEqual(tableId, nextProps.tableId)) return true
        if (!_.isEqual(tableSelector, nextProps.tableSelector)) return true
        if (!_.isEqual(filter, nextProps.filter)) return true

        return false
    }

    componentDidUpdate(prevProps) {
        const { level, locale, dataLocale } = this.props

        if (prevProps.level !== level) this.loadData()
        if (locale !== dataLocale && dataLocale) this.loadData()
    }

    loadData = () => {
        const { tableId, tableSelector, id, module, block, level, customData, companyAsCreditorLoadTableData } =
            this.props

        companyAsCreditorLoadTableData(tableId, tableSelector, id, module, block, level, customData)
    }

    handleTableChange = (changeData) => {
        const { tableId, tableSelector, id, module, block, level, customData, companyAsCreditorTableChange } =
            this.props

        companyAsCreditorTableChange(tableId, tableSelector, id, module, block, level, customData, changeData)
    }

    render() {
        const { intl, tableId, data, pagination, sorted, failed, isLoading, locked, filter } = this.props

        if (locked && !isLoading && !failed) {
            return (
                <div className={'cac-bankrupt'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.lockedTab` })} />
                </div>
            )
        }

        if (failed && !isLoading) {
            return (
                <div className={'cac-bankrupt'}>
                    <FailedLoad
                        customCallback={this.loadData}
                        customText={intl.formatMessage({ id: 'global.failedToLoadTabContent' })}
                        show
                    />
                </div>
            )
        }

        if (_.isEmpty(data) && !isLoading && _.isEmpty(filter?.activeFilters)) {
            return (
                <div className={'cac-bankrupt'}>
                    <NoData text={intl.formatMessage({ id: `${LOCALE_PREFIX}.noBankrupcyData` })} />
                </div>
            )
        }

        return (
            <div className={'cac-bankrupt'}>
                <FilterTable
                    id={tableId}
                    data={data ?? []}
                    loading={isLoading}
                    columnsDefinition={[
                        {
                            accessor: 'inactive',
                            id: 'activity',
                            Header: ({ Controls }) => (
                                <>
                                    {Controls}
                                    <InactivityIndicatorHeader />
                                </>
                            ),
                            filterable: true,
                            filter: {
                                type: 'VALUE',
                                value: _.map(
                                    _.get(filter, 'availableParameters.activity.availableValues', []),
                                    (x) => ({
                                        value: _.get(x, 'value', null),
                                        item: _.get(x, 'title', null),
                                    })
                                ),
                            },
                            Cell: (value) => <InactiveIndicatorCell inactive={_.get(value, 'value', false)} />,
                            width: 35,
                        },
                        {
                            accessor: 'debtor',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.debtor` }),
                            Cell: (value) => (
                                <div className={'centered-cell'}>
                                    <Entity
                                        entity={_.get(value, 'value', null)}
                                        inactive={_.get(value, 'value.inactive', false)}
                                        type={_.get(value, 'value.type', null)}
                                        value={_.get(value, 'value.name', null)}
                                        entId={_.get(value, 'value.id', null)}
                                        rowInactive={_.get(value, 'original.inactive')}
                                        caution={_.get(value, 'value.indicators.caution', null)}
                                        risk={_.get(value, 'value.indicators.risk', null)}
                                        notice={_.get(value, 'value.indicators.notice', null)}
                                    />
                                </div>
                            ),
                        },
                        {
                            accessor: 'folderId',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.folderId` }),
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    {_.get(value, 'value', '')}
                                </div>
                            ),
                            width: 140,
                            filterable: true,
                            filter: {
                                type: 'FULLTEXT',
                            },
                        },
                        {
                            accessor: 'event',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.eventName` }),
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    <Link
                                        label={_.get(value, 'value.label', '')}
                                        url={_.get(value, 'value.url', '')}
                                        showIcon
                                        iconType={'pdf'}
                                        openOnNewTab
                                    />
                                </div>
                            ),
                        },
                        {
                            accessor: 'startDate',
                            Header: intl.formatMessage({ id: `${LOCALE_PREFIX}.publishedDate` }),
                            Cell: (value) => (
                                <div
                                    className={cx(
                                        'centered-cell',
                                        _.get(value, 'original.inactive', false) && 'inactive'
                                    )}
                                >
                                    <CustomDate precision={'days'} date={_.get(value, 'value.value', null)} />
                                </div>
                            ),
                            width: 125,
                            sortable: true,
                        },
                    ]}
                    onTableChange={this.handleTableChange}
                    pageSizeOptions={[5, 10, 20, 50, 100]}
                    settings={{
                        pagination,
                        sorted,
                        filtered: (filter?.activeFilters ?? []).map((actFilter) => ({
                            id: actFilter?.id,
                            value: actFilter?.value || [],
                        })),
                    }}
                    texts={{
                        pagination: {
                            page: intl.formatMessage({ id: 'global.page' }),
                            from: intl.formatMessage({ id: 'global.of' }),
                            rowCount: intl.formatMessage({ id: 'global.rowCount' }),
                            rowShow: intl.formatMessage({ id: 'global.rowShow' }),
                            all: intl.formatMessage({ id: 'global.all' }),
                        },
                        filter: {
                            valueTitle: intl.formatMessage({ id: 'global.valueTitle' }),
                            fulltextTitle: intl.formatMessage({ id: 'global.fulltextTitle' }),
                        },
                    }}
                />
            </div>
        )
    }
}
